import React from "react";
import { PERFORMANCE, LINKS } from "../mock";
import { Eyebrow } from "./Hero";

function CoinBarChart() {
  const max = Math.max(...PERFORMANCE.coins.map((c) => c.value));
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-display text-sm" style={{ color: "#201850" }}>Trading Capital</span>
        <div className="flex items-center gap-3 text-[10px] font-mono-label" style={{ color: "rgba(32,24,80,0.5)" }}>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: "#00A8E8" }} />500 USDT</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: "#22C3F0" }} />14500</span>
        </div>
      </div>
      <div className="space-y-2">
        {PERFORMANCE.coins.map((c, i) => (
          <div key={i} className="coin-row flex items-center gap-2 rounded px-1 py-0.5">
            <span className="w-[74px] shrink-0 text-[10px] font-mono-label" style={{ color: "rgba(32,24,80,0.65)", letterSpacing: "0.05em" }}>
              {c.coin}
            </span>
            <div className="flex-1">
              <div className="coin-bar" style={{ width: `${(c.value / max) * 100}%` }} />
            </div>
            <span className="w-10 text-right text-[10px] font-semibold" style={{ color: "#0090CC" }}>
              {c.value.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Performance() {
  return (
    <section id="performance" data-testid="performance-section" className="relative py-14 md:py-16 sep-top">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="text-center mb-10">
          <Eyebrow text={PERFORMANCE.eyebrow} testId="performance-eyebrow" />
          <h2
            className="eyebrow-title-gap font-display leading-[1.05]"
            data-testid="performance-heading"
            style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", color: "#201850" }}
          >
            {PERFORMANCE.heading}
          </h2>
          <p className="performance-statement-red mt-3 italic text-base md:text-lg" data-testid="performance-statement">{PERFORMANCE.statement}</p>
          <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed md:text-base" data-testid="performance-intro" style={{ color: "rgba(32,24,80,0.72)" }}>{PERFORMANCE.intro}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          {/* Left: real Flourish USDT chart */}
          <div className="lg:col-span-4 flex flex-col" data-testid="perf-left-wrap">
            <p className="mb-3 text-center font-mono-label text-[10px]" style={{ color: "rgba(32,24,80,0.55)" }}>Profit · Per Coin · USDT</p>
            <div className="flex-1 rounded-lg overflow-hidden card-light" style={{ minHeight: "420px" }}>
              <iframe
                title="EazyBot Live Account Performance — USDT"
                src={LINKS.flourishUSDT}
                loading="lazy"
                allow="fullscreen"
                style={{ width: "100%", height: "100%", border: 0, display: "block" }}
              />
            </div>
          </div>

          {/* Center: Live Account Performance info card */}
          <div
            className="lg:col-span-4 rounded-2xl p-6 md:p-8 text-center"
            data-testid="perf-info-block"
            style={{
              background: "linear-gradient(180deg, rgba(0,168,232,0.10) 0%, rgba(34,195,240,0.05) 100%)",
              border: "1px solid rgba(0,168,232,0.35)",
            }}
          >
            <p className="font-mono-label text-[11px]" style={{ color: "#0090CC", letterSpacing: "0.35em" }}>2023 New</p>
            <h3 className="font-display mt-2 leading-[1]" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.1rem)", color: "#0090CC" }}>
              Live Account Performance
            </h3>
            <div className="mt-7 pt-6" style={{ borderTop: "1px solid rgba(32,24,80,0.1)" }} data-testid="perf-trades-closed">
              <p className="font-mono-label text-[10px]" style={{ color: "#0090CC" }}>Total Trades Closed</p>
              <div className="mt-3 rounded-md overflow-hidden card-light" style={{ height: "110px" }}>
                <iframe
                  title="Total Trades Closed — Live Counter"
                  src={LINKS.flourishTrades}
                  loading="lazy"
                  data-testid="perf-trades-counter"
                  style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                />
              </div>
            </div>
            <div className="mt-7 space-y-4 text-sm md:text-base leading-relaxed" style={{ color: "rgba(32,24,80,0.85)" }}>
              <p>
                The account began trading with <span className="font-semibold" style={{ color: "#0090CC" }}>500 USDT Per Coin</span> on <span className="font-semibold" style={{ color: "#0090CC" }}>January 1st, 2023</span> with the MAS Default Strategy.
              </p>
              <p className="font-display" style={{ color: "#0090CC", fontSize: "1.1rem" }}>Total Capital = 14,500 USDT</p>
            </div>
            <p className="mt-6 pt-5 text-[11px] italic" style={{ borderTop: "1px solid rgba(32,24,80,0.1)", color: "rgba(32,24,80,0.55)", whiteSpace: "pre-line" }}>
              {PERFORMANCE.disclaimer}
            </p>
          </div>

          {/* Right: real Flourish % chart */}
          <div className="lg:col-span-4 flex flex-col" data-testid="perf-right-wrap">
            <p className="mb-3 text-center font-mono-label text-[10px]" style={{ color: "rgba(32,24,80,0.55)" }}>Profit · Per Coin · %</p>
            <div className="flex-1 rounded-lg overflow-hidden card-light" style={{ minHeight: "420px" }}>
              <iframe
                title="EazyBot Live Account Performance — Percentage"
                src={LINKS.flourishPercent}
                loading="lazy"
                allow="fullscreen"
                style={{ width: "100%", height: "100%", border: 0, display: "block" }}
              />
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <a href={LINKS.performance} target="_blank" rel="noopener noreferrer" data-testid="performance-results-link" className="performance-link-pulse inline-flex max-w-full justify-center text-lg font-bold" style={{ color: "#0090CC" }}>VIEW MORE PERFORMANCE RESULTS</a>
          <p className="mt-4 text-[10px] font-mono-label" data-testid="performance-source" style={{ color: "rgba(32,24,80,0.45)" }}>SOURCE · EAZYBOT.INFO</p>
        </div>
      </div>
    </section>
  );
}
